import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { AlertCircle, Check, Copy, LoaderCircle, X } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { ApiClientError, type IntegrationReadiness } from '../api'

const focusableSelector = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

export function Dialog({
  open,
  title,
  description,
  children,
  footer,
  busy = false,
  onClose,
}: {
  open: boolean
  title: string
  description?: string
  children: ReactNode
  footer?: ReactNode
  busy?: boolean
  onClose: () => void
}) {
  const { t } = useTranslation()
  const titleId = useId()
  const descriptionId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  const onCloseRef = useRef(onClose)

  useEffect(() => {
    onCloseRef.current = onClose
  }, [onClose])

  useEffect(() => {
    if (!open) return

    const previousFocus = document.activeElement as HTMLElement | null
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const frame = window.requestAnimationFrame(() => {
      const preferred = dialogRef.current?.querySelector<HTMLElement>('[data-autofocus]')
      ;(preferred ?? dialogRef.current)?.focus()
    })

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !busy) {
        event.preventDefault()
        onCloseRef.current()
        return
      }
      if (event.key !== 'Tab' || !dialogRef.current) return

      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(focusableSelector)
      )
      if (focusable.length === 0) {
        event.preventDefault()
        dialogRef.current.focus()
        return
      }
      const first = focusable[0]!
      const last = focusable[focusable.length - 1]!
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => {
      window.cancelAnimationFrame(frame)
      document.removeEventListener('keydown', handleKeyDown)
      document.body.style.overflow = previousOverflow
      previousFocus?.focus()
    }
  }, [busy, open])

  if (!open) return null

  return createPortal(
    <div
      className='dialog-backdrop'
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !busy) onClose()
      }}
    >
      <div
        ref={dialogRef}
        className='dialog-card'
        role='dialog'
        aria-modal='true'
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <header className='dialog-header'>
          <div>
            <h2 id={titleId}>{title}</h2>
            {description ? <p id={descriptionId}>{description}</p> : null}
          </div>
          <button
            className='icon-button dialog-close'
            type='button'
            aria-label={t('Close')}
            disabled={busy}
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </header>
        <div className='dialog-body'>{children}</div>
        {footer ? <footer className='dialog-footer'>{footer}</footer> : null}
      </div>
    </div>,
    document.body
  )
}

export function PageHeader({
  title,
  description,
  actions,
}: {
  title: string
  description?: string
  actions?: ReactNode
}) {
  return (
    <header className='page-header'>
      <div>
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className='page-actions'>{actions}</div> : null}
    </header>
  )
}

export function Panel({
  title,
  description,
  children,
  actions,
  className = '',
  id,
}: {
  title?: string
  description?: string
  children: ReactNode
  actions?: ReactNode
  className?: string
  id?: string
}) {
  return (
    <section className={`panel ${className}`} id={id}>
      {title || actions ? (
        <header className='panel-header'>
          <div>
            {title ? <h2>{title}</h2> : null}
            {description ? <p>{description}</p> : null}
          </div>
          {actions}
        </header>
      ) : null}
      {children}
    </section>
  )
}

const stateClass = (state: string) => {
  if (['ready', 'active', 'verified', 'completed', 'resolved'].includes(state)) {
    return 'success'
  }
  if (
    ['failed', 'disabled', 'suspended', 'canceled', 'offline', 'quarantined', 'revoked', 'open'].includes(
      state
    )
  ) {
    return 'danger'
  }
  if (
    [
      'building',
      'deploying',
      'queued',
      'pending_dns',
      'uploaded',
      'pending',
      'leased',
      'draining',
      'issuing',
      'renewing',
      'contained',
    ].includes(state)
  ) {
    return 'warning'
  }
  return 'neutral'
}

export function StatusBadge({ state }: { state: string }) {
  const { t } = useTranslation()
  return <span className={`status status-${stateClass(state)}`}>{t(`state.${state}`)}</span>
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: ReactNode
}) {
  return (
    <div className='empty-state'>
      <div className='empty-mark' aria-hidden='true' />
      <h3>{title}</h3>
      <p>{description}</p>
      {action}
    </div>
  )
}

export function LoadingState() {
  const { t } = useTranslation()
  return (
    <div className='loading-state' role='status'>
      <LoaderCircle className='spin' size={18} />
      <span>{t('Loading')}</span>
    </div>
  )
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const { t } = useTranslation()
  const apiError = error instanceof ApiClientError ? error : null
  return (
    <div className='error-state' role='alert'>
      <AlertCircle size={20} />
      <div>
        <strong>{t('The request could not be completed')}</strong>
        <p>
          {t('Error code')}: <code>{apiError?.code ?? 'request_failed'}</code>
          {apiError?.requestId ? (
            <>
              {' · '}
              {t('Request ID')}: <code>{apiError.requestId}</code>
            </>
          ) : null}
        </p>
      </div>
      {retry ? (
        <button className='button button-secondary' type='button' onClick={retry}>
          {t('Retry')}
        </button>
      ) : null}
    </div>
  )
}

export function FeatureNotice({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  return (
    <div className='feature-notice'>
      <AlertCircle size={18} />
      <div>
        <strong>{t('This capability is disabled')}</strong>
        <p>{children}</p>
      </div>
    </div>
  )
}

export function CopyButton({ value }: { value: string }) {
  const { t } = useTranslation()
  const copy = async () => navigator.clipboard.writeText(value)
  return (
    <button
      className='icon-button'
      type='button'
      aria-label={t('Copy')}
      title={t('Copy')}
      onClick={() => void copy()}
    >
      <Copy size={15} />
    </button>
  )
}

export function IntegrationState({
  configured,
  state,
}: {
  configured?: boolean
  state?: IntegrationReadiness['status']
}) {
  const { t } = useTranslation()
  const effectiveState = state ?? (configured ? 'ready' : 'unconfigured')
  const ready = effectiveState === 'ready'
  const label =
    effectiveState === 'ready'
      ? t('Ready')
      : effectiveState === 'unavailable'
        ? t('Unavailable')
        : t('Not configured')
  return (
    <span className={`integration-state ${ready ? 'is-ready' : ''}`}>
      {ready ? <Check size={14} /> : <span aria-hidden='true'>—</span>}
      {label}
    </span>
  )
}
